/**
 * Country-name fallback map (used when Intl.DisplayNames is unavailable).
 */
export const COUNTRY_NAME_FALLBACK: Record<string, string> = {
  IN: 'India',
  US: 'United States',
  PR: 'Puerto Rico',
  CA: 'Canada',
  GB: 'United Kingdom',
  AU: 'Australia',
  AT: 'Austria',
  BE: 'Belgium',
  BG: 'Bulgaria',
  HR: 'Croatia',
  CY: 'Cyprus',
  CZ: 'Czechia',
  DK: 'Denmark',
  EE: 'Estonia',
  FI: 'Finland',
  FR: 'France',
  DE: 'Germany',
  GR: 'Greece',
  HU: 'Hungary',
  IE: 'Ireland',
  IT: 'Italy',
  LV: 'Latvia',
  LT: 'Lithuania',
  LU: 'Luxembourg',
  MT: 'Malta',
  NL: 'Netherlands',
  PL: 'Poland',
  PT: 'Portugal',
  RO: 'Romania',
  SK: 'Slovakia',
  SI: 'Slovenia',
  ES: 'Spain',
  SE: 'Sweden',
  CH: 'Switzerland',
  IS: 'Iceland',
  NO: 'Norway',
  MC: 'Monaco',
  AE: 'United Arab Emirates',
  SA: 'Saudi Arabia',
  MX: 'Mexico',
  ZA: 'South Africa',
  QA: 'Qatar',
  BH: 'Bahrain',
  KW: 'Kuwait',
  OM: 'Oman',
  JP: 'Japan',
  TH: 'Thailand',
  // Non-EU European countries (EU store)
  UA: 'Ukraine',
  RS: 'Serbia',
  JE: 'Jersey',
  AD: 'Andorra',
  AL: 'Albania',
  LI: 'Liechtenstein',
  BA: 'Bosnia & Herzegovina',
  MK: 'North Macedonia',
  MD: 'Moldova',
  XK: 'Kosovo',
  ME: 'Montenegro',
  SJ: 'Svalbard & Jan Mayen',
  GI: 'Gibraltar',
  IM: 'Isle of Man',
  GG: 'Guernsey',
  SM: 'San Marino',
  // Asia Pacific
  SG: 'Singapore',
  PH: 'Philippines',
  MY: 'Malaysia',
  TW: 'Taiwan',
  ID: 'Indonesia',
  VN: 'Vietnam',
  KR: 'South Korea',
  CN: 'China',
  HK: 'Hong Kong',
  MO: 'Macao',
  MN: 'Mongolia',
  KH: 'Cambodia',
  BN: 'Brunei',
  TL: 'Timor-Leste',
  NZ: 'New Zealand',
  FJ: 'Fiji',
  CK: 'Cook Islands',
  PF: 'French Polynesia',
  // South & Central Asia
  LK: 'Sri Lanka',
  MV: 'Maldives',
  NP: 'Nepal',
  BD: 'Bangladesh',
  KZ: 'Kazakhstan',
  UZ: 'Uzbekistan',
  KG: 'Kyrgyzstan',
  AM: 'Armenia',
  AZ: 'Azerbaijan',
  GE: 'Georgia',
  // Middle East (non-AE)
  IL: 'Israel',
  TR: 'Turkey',
  JO: 'Jordan',
  LB: 'Lebanon',
  // Americas
  CO: 'Colombia',
  BR: 'Brazil',
  CL: 'Chile',
  EC: 'Ecuador',
  AR: 'Argentina',
  PE: 'Peru',
  VE: 'Venezuela',
  PY: 'Paraguay',
  GY: 'Guyana',
  SR: 'Suriname',
  BO: 'Bolivia',
  UY: 'Uruguay',
  GF: 'French Guiana',
  GT: 'Guatemala',
  HN: 'Honduras',
  SV: 'El Salvador',
  NI: 'Nicaragua',
  CR: 'Costa Rica',
  PA: 'Panama',
  DO: 'Dominican Republic',
  TT: 'Trinidad & Tobago',
  BS: 'Bahamas',
  BB: 'Barbados',
  JM: 'Jamaica',
  BM: 'Bermuda',
  KY: 'Cayman Islands',
  AW: 'Aruba',
  GP: 'Guadeloupe',
  MQ: 'Martinique',
  TC: 'Turks & Caicos Islands',
  CW: 'Curaçao',
  MF: 'St. Martin',
  BL: 'St. Barthélemy',
  SX: 'Sint Maarten',
  GD: 'Grenada',
  GL: 'Greenland',
  // Africa
  NG: 'Nigeria',
  KE: 'Kenya',
  GH: 'Ghana',
  EG: 'Egypt',
  MA: 'Morocco',
  MU: 'Mauritius',
  CI: "Côte d'Ivoire",
  SN: 'Senegal',
  AO: 'Angola',
  BW: 'Botswana',
  NA: 'Namibia',
  TN: 'Tunisia',
  SC: 'Seychelles',
  ZM: 'Zambia',
  ZW: 'Zimbabwe',
  RW: 'Rwanda',
  BJ: 'Benin',
  GN: 'Guinea',
  // Other territories
  RE: 'Réunion',
  YT: 'Mayotte',
  TF: 'French Southern Territories',
  AX: 'Åland Islands',
  GLOBAL: 'Global',
};

let _displayNames: Intl.DisplayNames | null = null;

function getDisplayNames(): Intl.DisplayNames | null {
  if (!_displayNames) {
    try {
      _displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
    } catch {
      /* Intl not available */
    }
  }
  return _displayNames;
}

/**
 * Get formatted country name from country code
 */
export const getCountryName = (countryCode: string): string => {
  const code = countryCode.toUpperCase();
  // Check fallback map first for non-ISO codes like 'GLOBAL'
  if (COUNTRY_NAME_FALLBACK[code]) return COUNTRY_NAME_FALLBACK[code];
  try {
    return getDisplayNames()?.of(code) ?? code;
  } catch {
    return code;
  }
};
